import React, { useState, useEffect } from "react";
import { Button, Col, Row, Card, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { ReduxState } from "../../../redux";
import {
  resumeJob,
  getOveragePricing,
  loadPreferenceServer,
  savePreferenceServer,
  upgradeSubscriptionInPlace,
} from "../../../docuclipper/api";
import { setJobData } from "../../../redux/reducers/JobData";
import { createAlert } from "../../../redux/reducers/Alerts";
import { openModal } from "../../../redux/reducers/ModalManager";
import { fetchSubscription } from "../../../redux/reducers/Subscription";
import { fetchUser } from "../../../redux/reducers/User";
import {
  getNextUpgradeOption,
  groupPlansByTiers,
  calculateSavingsPercentage,
} from "../../Pages/Account/SelectPlanAfterTrial/tierUtils";

export const JobOutOfPages = ({ job }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const { subscription } = useSelector(
    (state: ReduxState) => state.Subscription,
  );
  const { plans } = useSelector((state: ReduxState) => state.Plans);
  const { user } = useSelector((state: ReduxState) => state.User);
  const neededPages = useSelector(
    (state: ReduxState) => state.JobData.job?.totalJobs,
  );

  const userPages = Number(user?.credits) + Number(user?.purchasedCredits);
  const includedPages = subscription?.docuclipperPlan?.numPages ?? 0;
  const multiplier =
    includedPages > 0 && neededPages
      ? Math.max(1, Math.round(Number(neededPages) / includedPages))
      : null;

  const [overagePricePerPage, setOveragePricePerPage] = useState<number | null>(
    null,
  );
  const [overageLimit, setOverageLimit] = useState<"unlimited" | number>(
    "unlimited",
  );
  const [overageConfigLoaded, setOverageConfigLoaded] = useState(false);
  const [currentOverageLimit, setCurrentOverageLimit] = useState<
    number | null | "unlimited"
  >(null);
  const [overageEnabled, setOverageEnabled] = useState<boolean>(false);
  const [confirmingOverage, setConfirmingOverage] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeConfirmOpen, setUpgradeConfirmOpen] = useState(false);
  // Excludes deprecated AnnuallyBilledMonthly (only Monthly | Annually are
  // valid cycles).
  const [selectedCycle, setSelectedCycle] = useState<"Monthly" | "Annually">(
    "Annually",
  );

  useEffect(() => {
    let cancelled = false;
    loadPreferenceServer({ key: "overageConfig" })
      .then((pref) => {
        if (cancelled || !pref?.value) {
          setCurrentOverageLimit(200);
          setOverageEnabled(false);
          setOverageConfigLoaded(true);
          return;
        }
        try {
          const obj = JSON.parse(pref.value);
          const pct = obj.limitPercent;
          setCurrentOverageLimit(
            pct === undefined || pct === null || pct === "unlimited"
              ? "unlimited"
              : pct,
          );
          setOverageEnabled(!!obj.enabled);
        } catch {
          setCurrentOverageLimit(200);
          setOverageEnabled(false);
        }
        setOverageConfigLoaded(true);
      })
      .catch(() => setOverageConfigLoaded(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const nextUpgrade =
    subscription?.status === "active" && plans?.length > 0 && includedPages > 0
      ? getNextUpgradeOption(plans, includedPages, selectedCycle)
      : null;

  // Compute savings % from real plan price data so the figure stays in sync
  // if pricing drifts. Falls back to 17 (current ratio) if both cycles aren't
  // available for the next tier.
  const savingsPercent = (() => {
    if (!nextUpgrade) return 17;
    const groups = groupPlansByTiers(plans || []);
    for (const g of groups) {
      for (const t of g.tiers) {
        if (t.numPages !== nextUpgrade.numPages) continue;
        const m = t.plans.Monthly?.amount;
        const a = t.plans.Annually?.amount;
        if (m && a) return calculateSavingsPercentage(m / 100, a / 100);
      }
    }
    return 17;
  })();

  // Hide the cycle toggle when only one cycle exists for the next tier.
  const bothCyclesAvailable = (() => {
    if (!nextUpgrade) return false;
    const groups = groupPlansByTiers(plans || []);
    for (const g of groups) {
      for (const t of g.tiers) {
        if (t.numPages !== nextUpgrade.numPages) continue;
        return !!(t.plans.Monthly && t.plans.Annually);
      }
    }
    return false;
  })();

  useEffect(() => {
    let cancelled = false;
    getOveragePricing()
      .then((r) => {
        if (!cancelled && r?.pricePerPage != null)
          setOveragePricePerPage(r.pricePerPage);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const headline =
      multiplier && multiplier >= 2
        ? `You've processed ${multiplier}× your plan's included pages.`
        : "You've used all of your plan's included pages.";
    dispatch(
      createAlert({
        timeout: 0,
        type: "info",
        messageWithUpdate: `${headline} Choose the best option below to continue.`,
        message: "",
      }),
    );
  }, [dispatch, multiplier]);

  const onConfirmOverageAndResume = async () => {
    if (!job) return;
    setConfirmingOverage(true);
    try {
      await savePreferenceServer({
        key: "overageConfig",
        value: JSON.stringify({
          enabled: true,
          limitPercent: overageLimit === "unlimited" ? null : overageLimit,
        }),
      });
      onResume();
    } catch (err) {
      dispatch(
        createAlert({
          message: "Failed to save extended limit setting. Please try again.",
          timeout: 0,
          type: "error",
        }),
      );
    } finally {
      setConfirmingOverage(false);
    }
  };

  const onConfirmUpgrade = async () => {
    if (!nextUpgrade || !job) return;
    const planName = nextUpgrade.plan.docuclipperPlan.name as string;
    const cycle =
      (nextUpgrade.plan.docuclipperPlan.billingCycle as string) || "Monthly";
    setUpgrading(true);
    try {
      await upgradeSubscriptionInPlace(planName, cycle);
      // Refresh subscription + user (credits) so downstream UI reflects the
      // new plan, then auto-resume the paused job.
      await Promise.all([
        dispatch(fetchSubscription({ force: true }) as any),
        dispatch(fetchUser() as any),
      ]);
      const resumed = await resumeJob(job.id);
      dispatch(setJobData({ job: resumed, progress: null }));
      dispatch(
        createAlert({
          message: `Upgraded to ${nextUpgrade.displayName}. Resuming your job.`,
          timeout: 0,
          type: "success",
        }),
      );
      setUpgradeConfirmOpen(false);
    } catch (err) {
      // Most likely cause: no payment method on file (trial/cancelled).
      // Fall back to the existing /account/upgrade flow which collects a
      // card via Stripe Checkout and lands back here with ?resumeJob=1.
      const returnUrl =
        typeof window !== "undefined" && job?.id
          ? `${window.location.origin}/jobData?jobId=${job.id}&resumeJob=1`
          : "";
      const nextParam = returnUrl
        ? `&next=${encodeURIComponent(returnUrl)}`
        : "";
      history.push(
        `/account/upgrade?upgradeTo=${encodeURIComponent(planName)}&billingCycle=${cycle}${nextParam}`,
      );
    } finally {
      setUpgrading(false);
    }
  };

  const onResume = () => {
    if (!job) return;
    resumeJob(job.id)
      .then((rsp) => {
        dispatch(setJobData({ job: rsp, progress: null }));
        dispatch(
          createAlert({
            message: "Processing resumed.",
            timeout: 0,
            type: "success",
          }),
        );
      })
      .catch((err) => {
        const detail =
          err?.errorMessage || err?.message || "Please try again.";
        dispatch(
          createAlert({
            message: `Couldn't resume the job. ${detail}`,
            timeout: 0,
            type: "error",
          }),
        );
      });
  };

  const estimatedOverageCost =
    overagePricePerPage != null &&
    neededPages != null &&
    includedPages > 0 &&
    neededPages > includedPages
      ? (neededPages - includedPages) * overagePricePerPage
      : null;

  return (
    <div style={{ maxWidth: "900px", margin: "0 auto" }}>
      <Row className="text-center mb-4">
        <Col>
          {subscription?.status === "trialing" ? (
            <>
              <p className="h5 text-muted mb-2">
                You've reached your trial page limit.
              </p>
              <p className="lead mb-3">
                Upgrade to a paid plan to continue processing this document.
              </p>
            </>
          ) : (
            <>
              <p className="h5 text-muted mb-2">
                {multiplier != null && multiplier >= 2
                  ? `You've processed ${multiplier}× your plan's included pages.`
                  : "You've used all of your plan's included pages."}
              </p>
              <p className="lead mb-3">
                Let's choose the best option for your continued processing.
              </p>
            </>
          )}
          {subscription?.status !== "trialing" && (
            <Button
              variant="link"
              size="sm"
              className="p-0"
              onClick={onResume}
            >
              Resume processing
            </Button>
          )}
        </Col>
      </Row>

      {/* Primary action — Upgrade (full-width hero) */}
      {nextUpgrade && (
        <Row className="mb-3">
          <Col>
            <Card className="border-success" style={{ borderWidth: 2 }}>
              <Card.Body>
                {bothCyclesAvailable && (
                  <div className="d-flex align-items-center mb-3 flex-wrap">
                    <div
                      className="btn-group btn-group-sm mr-2"
                      role="group"
                      aria-label="Billing cycle"
                    >
                      <Button
                        variant={
                          selectedCycle === "Monthly"
                            ? "success"
                            : "outline-secondary"
                        }
                        size="sm"
                        onClick={() => setSelectedCycle("Monthly")}
                      >
                        Monthly
                      </Button>
                      <Button
                        variant={
                          selectedCycle === "Annually"
                            ? "success"
                            : "outline-secondary"
                        }
                        size="sm"
                        onClick={() => setSelectedCycle("Annually")}
                      >
                        Annual
                      </Button>
                    </div>
                    {selectedCycle === "Annually" && (
                      <span className="badge badge-success">
                        save ~{savingsPercent}%
                      </span>
                    )}
                  </div>
                )}
                <Row className="align-items-center">
                  <Col md={7}>
                    <Card.Title className="text-success mb-1">
                      Upgrade to {nextUpgrade.displayName}
                    </Card.Title>
                    <p className="mb-1">
                      <strong>
                        {nextUpgrade.numPages.toLocaleString()} pages/mo
                      </strong>{" "}
                      at {nextUpgrade.priceDisplay}
                      {includedPages > 0 && (
                        <span className="text-muted">
                          {" "}
                          (vs {includedPages.toLocaleString()} on your current
                          plan)
                        </span>
                      )}
                    </p>
                    {estimatedOverageCost != null &&
                      estimatedOverageCost > 0 && (
                        <p className="small font-weight-bold text-success mb-1">
                          Save ~${estimatedOverageCost.toFixed(0)} this month
                          vs paying overages
                        </p>
                      )}
                    <p className="small text-muted mb-0">
                      Job resumes automatically. Cancel or change anytime.
                    </p>
                    {selectedCycle === "Annually" && bothCyclesAvailable && (
                      <p className="small text-muted mb-0">
                        Annual lock-in: cancel anytime, your pages don't
                        expire.
                      </p>
                    )}
                  </Col>
                  <Col md={5} className="text-md-right mt-3 mt-md-0">
                    {!upgradeConfirmOpen ? (
                      <Button
                        variant="success"
                        size="lg"
                        onClick={() => setUpgradeConfirmOpen(true)}
                      >
                        Upgrade to {nextUpgrade.displayName}
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="success"
                          size="lg"
                          className="mb-1"
                          onClick={onConfirmUpgrade}
                          disabled={upgrading}
                        >
                          {upgrading
                            ? "Upgrading..."
                            : `Confirm — ${nextUpgrade.priceDisplay}`}
                        </Button>
                        <div>
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => setUpgradeConfirmOpen(false)}
                            disabled={upgrading}
                          >
                            Cancel
                          </Button>
                        </div>
                      </>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {!nextUpgrade && (
        <Row className="mb-3">
          <Col>
            <Card className="border-success" style={{ borderWidth: 2 }}>
              <Card.Body className="d-flex justify-content-between align-items-center flex-wrap">
                <div>
                  <Card.Title className="text-success mb-1">
                    Upgrade your plan
                  </Card.Title>
                  <p className="small text-muted mb-0">
                    Lower your cost per page and get more headroom each month.
                  </p>
                </div>
                <Button
                  variant="success"
                  size="lg"
                  onClick={() => {
                    const returnUrl =
                      typeof window !== "undefined" && job?.id
                        ? `${window.location.origin}/jobData?jobId=${job.id}&resumeJob=1`
                        : "";
                    const nextParam = returnUrl
                      ? `?next=${encodeURIComponent(returnUrl)}`
                      : "";
                    history.push(`/account/upgrade${nextParam}`);
                  }}
                >
                  View plan options
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Secondary options — only render when at least one applies. Pack is
          gated to active subscribers (overflow use); offering it to trial /
          canceled users cannibalizes the subscription decision. Overages are
          always hidden for trialing users. */}
      {subscription?.status !== "trialing" && (
      <>
      <p className="small text-muted text-center mb-2">
        Or keep your current plan:
      </p>
      <Row className="mb-4">
        {/* Page Pack — active subscribers only (overflow purchases) */}
        {subscription?.status === "active" && (
        <Col md={6} className="mb-3">
          <Card className="h-100">
            <Card.Body className="py-3">
              <Card.Title className="h6 mb-1">Buy a one-off page pack</Card.Title>
              <p className="small text-muted mb-2">
                For one-time spikes. No commitment, pages never expire.
              </p>
              <Button
                variant="link"
                size="sm"
                className="p-0"
                onClick={() =>
                  dispatch(
                    openModal("buyPages", {
                      outOfPages: true,
                      returnPath: `${location.pathname}${location.search}${location.search ? "&" : "?"}resumeJob=1`,
                      jobId: job?.id,
                    }),
                  )
                }
              >
                Buy pages
              </Button>
            </Card.Body>
          </Card>
        </Col>
        )}

        {/* Overages — reframed as a setting on the existing plan */}
        {subscription?.status !== "trialing" && (
          <Col md={6} className="mb-3">
            <Card className="h-100">
              <Card.Body className="py-3">
                <Card.Title className="h6 mb-1">
                  Enable overages on this plan
                </Card.Title>
                <p className="small text-muted mb-2">
                  Continue at your plan's per-page rate
                  {overagePricePerPage != null
                    ? ` ($${overagePricePerPage.toFixed(2)}/page)`
                    : ""}
                  . Charged on your next invoice.
                </p>
                {overageConfigLoaded && (
                  <>
                    <p className="small mb-1">
                      Overages:{" "}
                      <strong>
                        {overageEnabled
                          ? currentOverageLimit === "unlimited"
                            ? "On (unlimited)"
                            : `On (${currentOverageLimit}% of plan)`
                          : "Off"}
                      </strong>
                    </p>
                    <div className="d-flex" style={{ gap: 8 }}>
                      <Form.Control
                        as="select"
                        value={
                          overageLimit === "unlimited"
                            ? "unlimited"
                            : String(overageLimit)
                        }
                        onChange={(e) => {
                          const v = e.target.value;
                          setOverageLimit(
                            v === "unlimited" ? "unlimited" : Number(v),
                          );
                        }}
                        size="sm"
                        style={{ maxWidth: 160 }}
                      >
                        <option value="unlimited">Unlimited</option>
                        <option value="200">200% of plan</option>
                        <option value="300">300% of plan</option>
                      </Form.Control>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={onConfirmOverageAndResume}
                        disabled={confirmingOverage}
                      >
                        {confirmingOverage ? "Saving..." : "Enable & continue"}
                      </Button>
                    </div>
                  </>
                )}
                {!overageConfigLoaded && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0"
                    onClick={() =>
                      history.push("/account?section=plan#overage")
                    }
                  >
                    Configure overages
                  </Button>
                )}
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>
      </>
      )}
    </div>
  );
};
